/**
 * SSO Provider Card Component
 *
 * Displays an SSO provider configuration with status, user count,
 * and action buttons for editing, testing, and toggling status.
 */

import React from 'react';
import { Card } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { SSOProviderType, SSOProviderStatus } from '../../../types/sso.types';
import type { SSOProviderConfig } from '../../../types/sso.types';

// ============================================================================
// TYPES
// ============================================================================

interface SSOProviderCardProps {
  provider: SSOProviderConfig;
  onEdit: (provider: SSOProviderConfig) => void;
  onToggleStatus: (provider: SSOProviderConfig) => void;
  onDelete: (provider: SSOProviderConfig) => void;
  onTest: (provider: SSOProviderConfig) => void;
  isUpdating?: boolean;
}

// ============================================================================
// HELPERS
// ============================================================================

const getProviderIcon = (providerType: string) => {
  if (providerType === SSOProviderType.MICROSOFT_ENTRA) {
    return (
      <svg className="h-8 w-8" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="1" y="1" width="9" height="9" fill="#F25022" />
        <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
        <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
        <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
      </svg>
    );
  }

  return (
    <svg className="h-8 w-8" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case SSOProviderStatus.ACTIVE:
      return <Badge variant="success" dot>Active</Badge>;
    case SSOProviderStatus.INACTIVE:
      return <Badge variant="neutral" dot>Inactive</Badge>;
    case SSOProviderStatus.PENDING_VERIFICATION:
      return <Badge variant="warning" dot>Pending Verification</Badge>;
    default:
      return <Badge variant="neutral">{status}</Badge>;
  }
};

const getProviderDisplayName = (providerType: string) => {
  switch (providerType) {
    case SSOProviderType.MICROSOFT_ENTRA:
      return 'Microsoft Entra ID';
    case SSOProviderType.GOOGLE:
      return 'Google Workspace';
    default:
      return providerType;
  }
};

// ============================================================================
// COMPONENT
// ============================================================================

export const SSOProviderCard: React.FC<SSOProviderCardProps> = ({
  provider,
  onEdit,
  onToggleStatus,
  onDelete,
  onTest,
  isUpdating = false,
}) => {
  const isActive = provider.status === SSOProviderStatus.ACTIVE;

  return (
    <Card variant="bordered">
      <Card.Body>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0">
              {getProviderIcon(provider.providerType)}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold text-gray-900">
                  {provider.displayName || getProviderDisplayName(provider.providerType)}
                </h3>
                {getStatusBadge(provider.status)}
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {isActive && provider.userCount !== undefined
                  ? `Connected -- ${provider.userCount} user${provider.userCount !== 1 ? 's' : ''} signed in via SSO`
                  : provider.status === SSOProviderStatus.PENDING_VERIFICATION
                  ? 'Verification pending'
                  : provider.status === SSOProviderStatus.INACTIVE
                  ? 'Provider is disabled'
                  : 'Not configured'}
              </p>
              {provider.allowedDomains && provider.allowedDomains.length > 0 && (
                <p className="text-xs text-gray-400 mt-0.5">
                  Domains: {provider.allowedDomains.join(', ')}
                </p>
              )}
              <div className="flex gap-2 mt-1">
                {provider.enforceSSO && (
                  <Badge variant="warning" size="sm">SSO Enforced</Badge>
                )}
                {provider.autoProvisionUsers && (
                  <Badge variant="info" size="sm">Auto-Provisioning</Badge>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onTest(provider)}
              disabled={isUpdating}
            >
              Test
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onToggleStatus(provider)}
              disabled={isUpdating}
            >
              {isActive ? 'Disable' : 'Enable'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onEdit(provider)}
              disabled={isUpdating}
            >
              Edit
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => onDelete(provider)}
              disabled={isUpdating}
            >
              Delete
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default SSOProviderCard;
